import { useMemo } from 'react';
import { cn } from '../../lib/cn.ts';
import { markdown } from './markdown.ts';

export interface DocumentMarkdownProps {
  children: string;
  className?: string;
}

/**
 * A long markdown document read on its own page, such as an insight report.
 * Same rendering as `ChatMarkdown`, but typeset for reading rather than for a
 * compact bubble: a heading scale, spaced blocks and ruled tables.
 */
export function DocumentMarkdown({ children, className }: DocumentMarkdownProps) {
  const html = useMemo(() => markdown.render(children), [children]);

  return (
    <div
      data-slot='document-markdown'
      className={cn(
        'text-md leading-relaxed text-ink-body',
        /* Text stays at a readable measure; tables may use the full width. */
        '[&>:not(table)]:max-w-[72ch]',
        '[&>:first-child]:mt-0 [&>:last-child]:mb-0',
        '[&_h1]:mt-10 [&_h1]:mb-3 [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:tracking-tight [&_h1]:text-ink',
        '[&_h2]:mt-8 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-ink',
        '[&_:is(h3,h4)]:mt-6 [&_:is(h3,h4)]:mb-1.5 [&_:is(h3,h4)]:text-md [&_:is(h3,h4)]:font-semibold [&_:is(h3,h4)]:text-ink',
        '[&_p]:my-3',
        '[&_:is(ul,ol)]:my-3 [&_:is(ul,ol)]:pl-5 [&_ul]:list-disc [&_ol]:list-decimal',
        '[&_li+li]:mt-1 [&_li>p]:my-0 [&_li_:is(ul,ol)]:my-1 [&_li]:marker:text-ink-faint',
        '[&_blockquote]:my-4 [&_blockquote]:border-l-2 [&_blockquote]:border-line [&_blockquote]:pl-4 [&_blockquote]:text-ink-muted',
        '[&_blockquote>:first-child]:mt-0 [&_blockquote>:last-child]:mb-0',
        '[&_hr]:my-8 [&_hr]:border-line',
        '[&_strong]:font-semibold [&_strong]:text-ink',
        '[&_em]:italic',
        '[&_a]:underline [&_a]:underline-offset-2',
        '[&_code]:rounded-xs [&_code]:bg-fill [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm',
        '[&_pre]:my-4 [&_pre]:overflow-x-auto [&_pre]:rounded-sm [&_pre]:bg-fill [&_pre]:p-3',
        '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
        /* `block` is what lets a table wider than the page scroll on its own. */
        '[&_table]:my-5 [&_table]:block [&_table]:w-max [&_table]:max-w-full [&_table]:overflow-x-auto',
        '[&_table]:text-base [&_table]:tabular-nums',
        '[&_:is(th,td)]:px-3 [&_:is(th,td)]:py-2 [&_:is(th,td):first-child]:pl-0',
        /* markdown-it's `text-align` on aligned columns is inline and still wins over this default. */
        '[&_th]:border-b [&_th]:border-line [&_th]:text-left [&_th]:align-bottom',
        '[&_th]:text-sm [&_th]:font-semibold [&_th]:text-ink-muted',
        '[&_td]:align-top [&_tbody_tr+tr]:border-t [&_tbody_tr+tr]:border-line',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
